import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Folder, FolderOpen, ImagePlus, Link2, Upload } from 'lucide-react';
import { useCreateNews, useNews, useUpdateNews } from '@/hooks/useNews';
import { newsApi } from '@/api/news';
import { bulletinApi, UploadBrowserResponse } from '@/api/bulletin';
import { Artikel } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RichTextEditor } from '@/components/editor/RichTextEditor';
import { notifyError, notifyInfo, notifySuccess } from '@/lib/feedback';
import { useNewsCategoryList } from '@/hooks/useNewsCategory';

type CoverMode = 'upload' | 'link' | 'existing';
const MAX_COVER_FILE_SIZE = 200 * 1024 * 1024;

export function NewsEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editingId = Number(id);
  const isEdit = Number.isFinite(editingId) && editingId > 0;

  const createBerita = useCreateNews();
  const updateBerita = useUpdateNews();
  const { data, isLoading } = useNews(editingId);
  const { data: categoryData, isLoading: isCategoryLoading } = useNewsCategoryList({ active: true, limit: 100 });

  const [form, setForm] = useState<Partial<Artikel>>({
    judul: '',
    isi: '',
    penulis: '',
    category: '',
    tanggal: '',
    cover: '',
  });
  const [coverDialogOpen, setCoverDialogOpen] = useState(false);
  const [coverMode, setCoverMode] = useState<CoverMode>('upload');
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverUrl, setCoverUrl] = useState('');
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [selectedUploadUrl, setSelectedUploadUrl] = useState('');
  const [uploadExplorer, setUploadExplorer] = useState<UploadBrowserResponse>({
    currentDir: '',
    parentDir: '',
    directories: [],
    files: [],
  });
  const [uploadsLoading, setUploadsLoading] = useState(false);

  const uploadLocalImage = async (file: File): Promise<string> => {
    try {
      const response = await newsApi.uploadInlineImage(file);
      notifySuccess('Gambar berita berhasil diunggah', undefined, false);
      return response.url || '';
    } catch (error) {
      notifyError('Gagal mengunggah gambar berita', error, false);
      throw error;
    }
  };

  useEffect(() => {
    if (!isEdit || !data) return;
    setForm({
      judul: data.judul || '',
      isi: data.isi || '',
      penulis: data.penulis || '',
      category: data.category || '',
      tanggal: data.tanggal || '',
      cover: data.cover || '',
    });
    setCoverUrl(data.cover || '');
    setCoverPreview(data.cover || '');
  }, [isEdit, data]);

  const applyCoverChoice = () => {
    if (coverMode === 'upload' && coverFile) {
      setCoverPreview(URL.createObjectURL(coverFile));
      setCoverUrl('');
      setSelectedUploadUrl('');
    }
    if (coverMode === 'link' && coverUrl.trim()) {
      setCoverPreview(coverUrl.trim());
      setCoverFile(null);
      setSelectedUploadUrl('');
    }
    if (coverMode === 'existing' && selectedUploadUrl.trim()) {
      setCoverPreview(selectedUploadUrl.trim());
      setCoverFile(null);
      setCoverUrl(selectedUploadUrl.trim());
    }
    setCoverDialogOpen(false);
  };

  const loadUploads = async (dir = '') => {
    setUploadsLoading(true);
    try {
      const data = await bulletinApi.browseUploads(dir);
      setUploadExplorer(data);
    } catch (error) {
      notifyError('Gagal memuat daftar upload', error, false);
      throw error;
    } finally {
      setUploadsLoading(false);
    }
  };

  useEffect(() => {
    if (!coverDialogOpen || coverMode !== 'existing') return;
    loadUploads(uploadExplorer.currentDir || '').catch((error) => {
      console.error('Failed to browse uploads:', error);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverDialogOpen, coverMode]);

  const categoryOptions = useMemo(() => {
    const items = categoryData?.data ? [...categoryData.data] : [];
    const currentCategory = (form.category || '').trim();

    if (currentCategory && !items.some((item) => item.name === currentCategory)) {
      items.unshift({
        id: 0,
        name: currentCategory,
        isActive: true,
        sortOrder: -1,
      });
    }

    return items;
  }, [categoryData?.data, form.category]);

  const handleCoverFileChange = (file: File | null) => {
    if (!file) {
      setCoverFile(null);
      return;
    }

    if (file.size > MAX_COVER_FILE_SIZE) {
      notifyInfo('Ukuran cover maksimal 200 MB', 'Pilih file yang lebih kecil sebelum menyimpan.', false);
      setCoverFile(null);
      return;
    }

    setCoverFile(file);
  };

  const hasCoverSelected = Boolean(
    coverFile
    || coverPreview?.trim()
    || (coverMode === 'link' && coverUrl.trim())
    || (coverMode === 'existing' && selectedUploadUrl.trim())
    || form.cover?.trim()
  );

  const submit = async () => {
    if (!form.judul?.trim() || !hasCoverSelected) return;

    const payload: Partial<Artikel> = {
      judul: form.judul?.trim(),
      isi: form.isi || '',
      penulis: form.penulis?.trim() || '',
      category: form.category?.trim() || '',
      tanggal: form.tanggal || '',
      cover: coverMode === 'link'
        ? (coverUrl.trim() || '')
        : coverMode === 'existing'
          ? (selectedUploadUrl.trim() || '')
          : undefined,
    };

    try {
      let savedId = editingId;
      if (isEdit) {
        await updateBerita.mutateAsync({ id: editingId, data: payload });
      } else {
        const result = await createBerita.mutateAsync(payload);
        savedId = Number(result?.id || 0);
      }

      if (savedId <= 0) {
        notifyInfo('Berita tersimpan tanpa ID respons');
        return;
      }

      if (coverFile) {
        const uploadResult = await newsApi.uploadCover(savedId, coverFile);
        const persistedCover = uploadResult.cover || '';
        if (persistedCover) {
          setCoverPreview(persistedCover);
          setCoverUrl(persistedCover);
          setForm((prev) => ({ ...prev, cover: persistedCover }));
        }
        notifySuccess('Cover berita berhasil diunggah');
      }

      navigate('/news');
    } catch (error) {
      console.error('Failed to save news:', error);
    }
  };

  if (isEdit && isLoading) {
    return <div>Memuat...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="mb-2">
            <Link to="/news" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-4 w-4" />
              Kembali ke Berita
            </Link>
          </div>
          <h2 className="text-3xl font-bold tracking-tight">{isEdit ? 'Ubah Berita' : 'Buat Berita'}</h2>
          <p className="text-muted-foreground">Tulis dan publikasikan konten berita.</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Detail Berita</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="judul">Judul</Label>
            <Input
              id="judul"
              value={form.judul || ''}
              onChange={(e) => setForm((v) => ({ ...v, judul: e.target.value }))}
              placeholder="Judul berita"
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="penulis">Penulis</Label>
              <Input
                id="penulis"
                value={form.penulis || ''}
                onChange={(e) => setForm((v) => ({ ...v, penulis: e.target.value }))}
                placeholder="Penulis name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tanggal">Tanggal</Label>
              <Input
                id="tanggal"
                type="date"
                value={form.tanggal || ''}
                onChange={(e) => setForm((v) => ({ ...v, tanggal: e.target.value }))}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="category">Kategori</Label>
            <Select
              value={(form.category || '').trim() || '__none__'}
              onValueChange={(value) => setForm((v) => ({ ...v, category: value === '__none__' ? '' : value }))}
              disabled={isCategoryLoading}
            >
              <SelectTrigger id="category">
                <SelectValue placeholder="Pilih kategori berita" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Tanpa kategori</SelectItem>
                {categoryOptions.map((item) => (
                  <SelectItem key={`${item.id}-${item.name}`} value={item.name}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Atur daftar kategori di <Link to="/news-categories" className="underline">Kategori Berita</Link>.
            </p>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <Label className="flex items-center gap-1">
                Cover Berita
                <span className="text-destructive">*</span>
              </Label>
              <Button variant="outline" type="button" onClick={() => setCoverDialogOpen(true)}>
                <ImagePlus className="mr-2 h-4 w-4" />
                Pilih Cover
              </Button>
            </div>
            {coverPreview ? (
              <img src={coverPreview} alt="Cover berita" className="h-56 w-full rounded-md object-cover" />
            ) : (
              <div className="flex h-56 w-full items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
                Belum ada cover dipilih
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Upload cover lokal maksimal 200 MB. Format yang disarankan: JPG, PNG, atau WebP.
            </p>
          </div>
          <div className="space-y-2">
            <Label>Konten</Label>
            <RichTextEditor
              value={form.isi || ''}
              onChange={(html) => setForm((v) => ({ ...v, isi: html }))}
              placeholder="Tulis konten berita..."
              onUploadLocalImage={uploadLocalImage}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/news')}>Batal</Button>
        <Button onClick={submit} disabled={!form.judul?.trim() || !hasCoverSelected}>
          Simpan
        </Button>
      </div>

      <Dialog open={coverDialogOpen} onOpenChange={setCoverDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pilih Cover</DialogTitle>
            <DialogDescription>Gunakan file lokal atau URL gambar.</DialogDescription>
          </DialogHeader>
          <Tabs value={coverMode} onValueChange={(v) => setCoverMode(v as CoverMode)}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="upload">
                <Upload className="mr-2 h-4 w-4" />
                Upload Lokal
              </TabsTrigger>
              <TabsTrigger value="link">
                <Link2 className="mr-2 h-4 w-4" />
                Link URL
              </TabsTrigger>
              <TabsTrigger value="existing">
                <Folder className="mr-2 h-4 w-4" />
                Dari Server
              </TabsTrigger>
            </TabsList>
            <TabsContent value="upload" className="space-y-3 pt-3">
              <Input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => handleCoverFileChange(e.target.files?.[0] || null)}
              />
              <p className="text-xs text-muted-foreground">Maksimal 200 MB. Format yang disarankan: JPG, PNG, atau WebP.</p>
            </TabsContent>
            <TabsContent value="link" className="space-y-3 pt-3">
              <Input
                value={coverUrl}
                onChange={(e) => setCoverUrl(e.target.value)}
                placeholder="https://example.com/cover.jpg"
              />
            </TabsContent>
            <TabsContent value="existing" className="space-y-3 pt-3">
              <div className="flex items-center justify-between rounded border p-2 text-xs">
                <div className="truncate">
                  {uploadExplorer.currentDir ? `uploads/${uploadExplorer.currentDir}` : 'uploads/'}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => loadUploads(uploadExplorer.parentDir || '')}
                    disabled={uploadsLoading || uploadExplorer.currentDir === ''}
                  >
                    <FolderOpen className="mr-1 h-4 w-4" />
                    Naik
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => loadUploads(uploadExplorer.currentDir || '')}
                    disabled={uploadsLoading}
                  >
                    Muat Ulang
                  </Button>
                </div>
              </div>
              <div className="max-h-72 space-y-2 overflow-y-auto rounded border p-2">
                {uploadsLoading ? (
                  <p className="text-sm text-muted-foreground">Memuat folder...</p>
                ) : (
                  <>
                    {uploadExplorer.directories.map((dir) => (
                      <button
                        key={`dir-${dir.dir}`}
                        type="button"
                        className="flex w-full items-center rounded px-2 py-1 text-left text-sm hover:bg-muted"
                        onClick={() => loadUploads(dir.dir)}
                      >
                        <Folder className="mr-2 h-4 w-4" />
                        {dir.name}
                      </button>
                    ))}
                    {uploadExplorer.files.length > 0 ? (
                      <div className="grid grid-cols-2 gap-2 pt-2">
                        {uploadExplorer.files.map((file) => {
                          const selected = selectedUploadUrl === file.url;
                          return (
                            <button
                              key={file.url}
                              type="button"
                              className={`overflow-hidden rounded border text-left ${selected ? 'ring-2 ring-primary' : ''}`}
                              onClick={async () => {
                                try {
                                  const copied = await bulletinApi.copyUploadImage(file.path, 'cover');
                                  const copiedUrl = copied.url || '';
                                  setSelectedUploadUrl(copiedUrl);
                                  setCoverUrl(copiedUrl);
                                  setCoverPreview(copiedUrl);
                                  notifySuccess('Gambar server disalin ke folder publik', undefined, false);
                                } catch (error) {
                                  notifyError('Gagal menyalin gambar server', error, false);
                                }
                              }}
                            >
                              <img src={file.url} alt={file.name} className="h-24 w-full object-cover" />
                              <div className="truncate px-2 py-1 text-xs">{file.name}</div>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Belum ada file gambar di folder ini.</p>
                    )}
                  </>
                )}
              </div>
              {selectedUploadUrl ? (
                <div className="space-y-2 rounded border p-2">
                  <p className="text-xs text-muted-foreground">Preview terpilih:</p>
                  <img src={selectedUploadUrl} alt="Selected upload" className="max-h-44 w-full rounded border object-contain" />
                </div>
              ) : null}
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCoverDialogOpen(false)}>
              Batal
            </Button>
            <Button onClick={applyCoverChoice}>Pakai Cover Ini</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
